import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
  FiPlus, FiEdit2, FiTrash2, FiMapPin, FiSearch, FiMail, FiPhone, FiX, FiGift,
  FiCheckCircle, FiAlertCircle,
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import {
  getDerasarPersons, createDerasarPerson, updateDerasarPerson,
  deleteDerasarPerson, getAddressBookList, requestPersonEmailVerification,
} from '../../services/api';
import { normalizeDateOnly, formatDateShort } from '../../utils/dateUtils';
import PersonPhoneVerificationModal from './PersonPhoneVerificationModal';
import styles from './PersonsPage.module.css';

const empty = {
  name: '', phone: '', email: '', panCard: '', aadhaarCard: '',
  addressBookId: '',
  specialDates: [],
};

const getInitials = (name) => (name || '?')
  .split(' ')
  .map((part) => part[0])
  .slice(0, 2)
  .join('')
  .toUpperCase();

const formatAddress = (obj) => {
  if (!obj) return '';
  if (!obj.flatNo && !obj.buildingName && !obj.addressLine1) return '';

  return [
    obj.flatNo && obj.buildingName ? `${obj.flatNo}, ${obj.buildingName}` : (obj.flatNo || obj.buildingName),
    obj.addressLine1,
    obj.addressLine2,
    obj.city,
    obj.state,
    obj.pincode ? `- ${obj.pincode}` : '',
    obj.country && obj.country !== 'India' ? obj.country : '',
  ].filter(Boolean).join(', ');
};

const resolveAddress = (person) => {
  if (person.addressBookEntry) return formatAddress(person.addressBookEntry);
  return formatAddress(person);
};

const resolveAddressLabel = (person) => person.addressBookEntry?.label || null;

const PersonRow = ({
  person, isExpanded, onToggle, onEdit, onDelete, onVerifyEmail, onVerifyPhone, rowRef,
}) => {
  const address = resolveAddress(person);
  const addressLabel = resolveAddressLabel(person);

  return (
    <>
      <tr className={styles.memberRowClickable} onClick={onToggle} ref={rowRef}>
        <td>
          <div className={styles.memberCell}>
            <div className={styles.memberAvatar}>{getInitials(person.name)}</div>
            <div className={styles.memberDropdownItemInfo}>
              <div className={styles.memberDropdownItemName}>{person.name}</div>
            </div>
          </div>
        </td>

        <td>
          {person.phone ? (
            <span className={styles.contactCell}>
              <FiPhone size={11} />
              {person.phone}
              {person.isPhoneVerified ? (
                <FiCheckCircle size={11} className={styles.inlineVerifiedIcon} title="Phone verified" />
              ) : (
                <FiAlertCircle
                  size={11}
                  className={styles.inlineUnverifiedIcon}
                  title="Not verified — notifications will go to your contact instead"
                />
              )}
            </span>
          ) : (
            <span className={styles.muted}>-</span>
          )}
        </td>

        <td>
          {person.email ? (
            <span className={styles.contactCell}>
              <FiMail size={11} />
              {person.email}
              {person.isEmailVerified ? (
                <FiCheckCircle size={11} className={styles.inlineVerifiedIcon} title="Email verified" />
              ) : (
                <FiAlertCircle size={11} className={styles.inlineUnverifiedIcon} title="Email not verified" />
              )}
            </span>
          ) : (
            <span className={styles.muted}>-</span>
          )}
        </td>

        <td className={styles.addressCell}>
          {addressLabel ? (
            <span title={`${addressLabel}${address ? ` - ${address}` : ''}`}>
              <FiMapPin size={11} className={styles.addressIcon} />
              {addressLabel}
              {address ? ` - ${address}` : ''}
            </span>
          ) : address ? (
            <span title={address}>
              <FiMapPin size={11} className={styles.addressIcon} />
              {address}
            </span>
          ) : (
            <span className={styles.muted}>-</span>
          )}
        </td>

        <td className={styles.actionsCell} onClick={(e) => e.stopPropagation()}>
          <div className={styles.memberActions}>
            <button className={`${styles.btn} ${styles.btnEdit}`} onClick={() => onEdit(person)}>
              <FiEdit2 size={12} /> Edit
            </button>
            <button className={`${styles.btn} ${styles.btnDelete}`} onClick={() => onDelete(person)}>
              <FiTrash2 size={12} /> Remove
            </button>
          </div>
        </td>
      </tr>

      {isExpanded && (
        <tr className={styles.detailsRow}>
          <td colSpan={5}>
            <div className={styles.memberDetails}>
              <div className={styles.detailsGrid}>
                <div className={styles.detailsItem}>
                  <span className={styles.detailsLabel}>Phone</span>
                  <span className={styles.detailsValue}>
                    {person.phone ? (
                      <>
                        <FiPhone size={11} className={styles.valueIcon} />{person.phone}
                        {person.isPhoneVerified ? (
                          <span className={styles.verifiedBadge}><FiCheckCircle size={11} /> Verified</span>
                        ) : (
                          <>
                            <span className={styles.unverifiedBadge}><FiAlertCircle size={11} /> Not verified</span>
                            <button
                              type="button"
                              className={styles.verifyBtn}
                              onClick={(e) => { e.stopPropagation(); onVerifyPhone(person); }}
                            >
                              Verify
                            </button>
                          </>
                        )}
                      </>
                    ) : '-'}
                  </span>
                  {person.phone && !person.isPhoneVerified && (
                    <span className={styles.detailsHint}>
                      Not verified — notifications will go to your contact instead.
                    </span>
                  )}
                </div>
                <div className={styles.detailsItem}>
                  <span className={styles.detailsLabel}>Email</span>
                  <span className={styles.detailsValue}>
                    {person.email ? (
                      <>
                        <FiMail size={11} className={styles.valueIcon} />{person.email}
                        {person.isEmailVerified ? (
                          <span className={styles.verifiedBadge}><FiCheckCircle size={11} /> Verified</span>
                        ) : (
                          <>
                            <span className={styles.unverifiedBadge}><FiAlertCircle size={11} /> Not verified</span>
                            <button
                              type="button"
                              className={styles.verifyBtn}
                              onClick={(e) => { e.stopPropagation(); onVerifyEmail(person); }}
                            >
                              Verify
                            </button>
                          </>
                        )}
                      </>
                    ) : '-'}
                  </span>
                </div>
                {person.panCard && (
                  <div className={styles.detailsItem}>
                    <span className={styles.detailsLabel}>PAN Card</span>
                    <span className={styles.detailsValue}>{person.panCard}</span>
                  </div>
                )}
                {person.aadhaarCard && (
                  <div className={styles.detailsItem}>
                    <span className={styles.detailsLabel}>Aadhaar</span>
                    <span className={styles.detailsValue}>{person.aadhaarCard}</span>
                  </div>
                )}
                <div className={`${styles.detailsItem} ${styles.detailsFullRow}`}>
                  <span className={styles.detailsLabel}>
                    Address {addressLabel && <em className={styles.addressLabelHint}>({addressLabel})</em>}
                  </span>
                  <span className={styles.detailsValue}>
                    <FiMapPin size={12} className={styles.valueIcon} />
                    {address || <span className={styles.detailsValueEmpty}>No address on record</span>}
                  </span>
                </div>
                {person.specialDates && person.specialDates.length > 0 && (
                  <div className={`${styles.detailsItem} ${styles.detailsFullRow}`}>
                    <span className={styles.detailsLabel}>Special Dates</span>
                    <span className={styles.detailsValue}>
                      <div className={styles.specialDatesList}>
                        {person.specialDates.map((d) => (
                          <span key={d.id} className={styles.specialDateChip}>
                            {d.label} - {formatDateShort(d.date)}
                          </span>
                        ))}
                      </div>
                    </span>
                  </div>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default function PersonsPage() {
  const { activeDerasarId, user } = useAuth();
  const [persons, setPersons] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [expandedPersonId, setExpandedPersonId] = useState(null);
  const [phoneVerifyTarget, setPhoneVerifyTarget] = useState(null);
  const [personsBannerDismissed, setPersonsBannerDismissed] = useState(false);
  const [pendingScrollPersonId, setPendingScrollPersonId] = useState(null);

  const load = useCallback(async () => {
    if (!activeDerasarId) return;
    try {
      const [pRes, aRes] = await Promise.all([
        getDerasarPersons(activeDerasarId),
        getAddressBookList(activeDerasarId),
      ]);
      const nextPersons = Array.isArray(pRes.data) ? pRes.data : [];
      const nextAddresses = Array.isArray(aRes.data) ? aRes.data : [];
      const defaultAddress = nextAddresses.find((address) => address.isDefault);
      setPersons(nextPersons);
      setAddresses(nextAddresses);
      setForm((current) => {
        if (current.addressBookId && nextAddresses.some((address) => String(address.id) === String(current.addressBookId))) {
          return current;
        }
        return {
          ...current,
          addressBookId: defaultAddress ? String(defaultAddress.id) : current.addressBookId,
        };
      });
    } catch {
      toast.error('Failed to load persons');
    }
  }, [activeDerasarId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!user?.id || !activeDerasarId) return;
    setPersonsBannerDismissed(sessionStorage.getItem(`personsReminderDismissed:${user.id}:${activeDerasarId}`) === '1');
  }, [user?.id, activeDerasarId]);

  const filtered = useMemo(() => {
    const query = search.toLowerCase().trim();

    return persons.filter((person) => {
      if (!query) return true;

      const address = resolveAddress(person);
      return [
        person.name,
        person.phone,
        person.email,
        person.panCard,
        person.aadhaarCard,
        address,
        resolveAddressLabel(person),
      ].some((value) => (value || '').toLowerCase().includes(query));
    });
  }, [persons, search]);

  const getRowRef = (person) => {
    if (person.id !== pendingScrollPersonId) return undefined;
    return (node) => {
      if (!node) return;
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setPendingScrollPersonId(null);
    };
  };

  const unverifiedContactCount = useMemo(
    () => persons.filter((person) => (person.phone && !person.isPhoneVerified) || (person.email && !person.isEmailVerified)).length,
    [persons],
  );

  const dismissPersonsBanner = () => {
    if (user?.id && activeDerasarId) sessionStorage.setItem(`personsReminderDismissed:${user.id}:${activeDerasarId}`, '1');
    setPersonsBannerDismissed(true);
  };

  const openAdd = () => {
    const defaultAddress = addresses.find((address) => address.isDefault);
    setEditing(null);
    setForm({ ...empty, specialDates: [], addressBookId: defaultAddress ? String(defaultAddress.id) : '' });
    setShowModal(true);
  };

  const openEdit = (person) => {
    const defaultAddress = addresses.find((address) => address.isDefault);
    setEditing(person);
    setForm({
      name: person.name,
      phone: person.phone || '',
      email: person.email || '',
      panCard: person.panCard || '',
      aadhaarCard: person.aadhaarCard || '',
      addressBookId: person.addressBookId ? String(person.addressBookId) : (defaultAddress ? String(defaultAddress.id) : ''),
      specialDates: (person.specialDates || []).map((d) => ({
        label: d.label,
        date: normalizeDateOnly(d.date),
      })),
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    if (!form.addressBookId) return toast.error('Select an address');
    if (form.specialDates.some((d) => Boolean(d.label.trim()) !== Boolean(d.date)))
      return toast.error('Each special date requires both a label and a date');

    setSaving(true);
    try {
      const specialDates = form.specialDates
        .map((d) => ({ label: (d.label || '').trim(), date: d.date || '' }))
        .filter((d) => d.label && d.date);

      const payload = {
        derasarId: activeDerasarId,
        name: form.name.trim(),
        phone: form.phone || null,
        email: form.email || null,
        panCard: form.panCard || null,
        aadhaarCard: form.aadhaarCard || null,
        addressBookId: form.addressBookId || null,
        specialDates,
        flatNo: '',
        buildingName: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        pincode: '',
        country: 'India',
      };

      if (editing) {
        await updateDerasarPerson(editing.id, payload);
        toast.success('Person updated');
        setShowModal(false);
        load();
      } else {
        const created = await createDerasarPerson(payload);
        const newPersonId = created.data?.id;
        const hasPhone = Boolean(payload.phone);
        const hasEmail = Boolean(payload.email);

        if (hasPhone && hasEmail) {
          toast.success('Person added — verify their phone and email below to enable direct notifications.');
        } else if (hasPhone) {
          toast.success('Person added — verify their phone below to enable direct notifications.');
        } else if (hasEmail) {
          toast.success('Person added — verify their email below to enable direct notifications.');
        } else {
          toast.success('Person added');
        }

        setShowModal(false);
        // Awaited (unlike the edit path below) so the new row exists in `persons`
        // before we try to expand/scroll to it.
        await load();

        if (newPersonId && (hasPhone || hasEmail)) {
          setSearch('');
          setExpandedPersonId(newPersonId);
          setPendingScrollPersonId(newPersonId);
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteDerasarPerson(deleteTarget.id);
      toast.success('Person removed');
      setDeleteTarget(null);
      setExpandedPersonId((current) => (current === deleteTarget.id ? null : current));
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Delete failed');
    }
  };

  const handleVerifyEmail = async (person) => {
    try {
      const res = await requestPersonEmailVerification(person.id);
      toast.success(`Verification link sent to ${res.data.email}`);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to send verification link');
    }
  };

  const set = (field) => (e) => {
    setForm((current) => ({
      ...current,
      [field]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    }));
  };

  const addSpecialDate = () => {
    setForm((current) => ({
      ...current,
      specialDates: [...current.specialDates, { label: '', date: '' }],
    }));
  };

  const updateSpecialDate = (index, field, value) => {
    setForm((current) => ({
      ...current,
      specialDates: current.specialDates.map((d, i) => (i === index ? { ...d, [field]: value } : d)),
    }));
  };

  const removeSpecialDate = (index) => {
    setForm((current) => ({
      ...current,
      specialDates: current.specialDates.filter((_, i) => i !== index),
    }));
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>My Persons</h1>
        <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={openAdd}>
          <FiPlus size={14} /> Add Person
        </button>
      </div>

      {unverifiedContactCount > 0 && !personsBannerDismissed && (
        <div className={styles.personsReminderBanner}>
          <span className={styles.personsReminderText}>
            <FiAlertCircle size={16} />
            {unverifiedContactCount} of your persons have unverified contact info. Notifications for them will go to your own contact until verified.
          </span>
          <button
            className={styles.personsReminderDismiss}
            onClick={dismissPersonsBanner}
            aria-label="Dismiss"
            type="button"
          >
            <FiX size={16} />
          </button>
        </div>
      )}

      <div className={styles.sectionCard}>
        <div className={styles.sectionHeader}>
          My Persons
          <span className={styles.countBadge}>{filtered.length}</span>
        </div>

        <div className={styles.controls}>
          <div className={styles.searchBar}>
            <FiSearch className={styles.searchIcon} />
            <input
              className={styles.searchInput}
              placeholder="Search by name, phone, email, PAN, Aadhaar, address..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className={styles.emptyState}>
            {search ? 'No persons match your search.' : 'No persons found. Add one using the button above.'}
          </div>
        ) : (
          <div className={styles.memberTableContainer}>
            <table className={styles.memberTable}>
              <colgroup>
                <col className={styles.personCol} />
                <col className={styles.phoneCol} />
                <col className={styles.emailCol} />
                <col className={styles.addressCol} />
                <col className={styles.actionsCol} />
              </colgroup>
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Address</th>
                  <th className={styles.actionsCell}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((person) => (
                  <PersonRow
                    key={person.id}
                    person={person}
                    isExpanded={expandedPersonId === person.id}
                    onToggle={() => setExpandedPersonId((current) => (current === person.id ? null : person.id))}
                    onEdit={openEdit}
                    onDelete={setDeleteTarget}
                    onVerifyEmail={handleVerifyEmail}
                    onVerifyPhone={setPhoneVerifyTarget}
                    rowRef={getRowRef(person)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className={styles.modal} onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div className={styles.modalBox}>
            <div className={styles.modalTitle}>{editing ? 'Edit Person' : 'Add Person'}</div>

            <div className={styles.formGrid}>
              <div className={`${styles.formGroup} ${styles.formGridFull}`}>
                <label className={styles.label}>Name *</label>
                <input className={styles.input} value={form.name} onChange={set('name')} placeholder="Full name" />
              </div>

              <div className={`${styles.formGroup} ${styles.formGridFull}`}>
                <label className={styles.label}>Address</label>
                <select className={styles.select} value={form.addressBookId} onChange={set('addressBookId')}>
                  <option value="">- Pick from address book -</option>
                  {addresses.map((address) => (
                    <option key={address.id} value={String(address.id)}>
                      {address.label}{address.isDefault ? ' (Default)' : ''} — {formatAddress(address) || 'No address details'}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Phone</label>
                <input className={styles.input} value={form.phone} onChange={set('phone')} placeholder="Phone number" />
                {form.phone && (!editing || !editing.isPhoneVerified) && (
                  <div className={styles.verifyAlert}>
                    <FiAlertCircle size={13} />
                    <span>Verify this phone number so this person receives donation and payment notifications directly.</span>
                  </div>
                )}
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Email</label>
                <input className={styles.input} value={form.email} onChange={set('email')} placeholder="Email address" />
                {form.email && (!editing || !editing.isEmailVerified) && (
                  <div className={styles.verifyAlert}>
                    <FiAlertCircle size={13} />
                    <span>Verify this email so this person receives donation and payment notifications directly.</span>
                  </div>
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>PAN Card</label>
                <input className={styles.input} value={form.panCard} onChange={set('panCard')} placeholder="Optional" />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Aadhaar Card</label>
                <input className={styles.input} value={form.aadhaarCard} onChange={set('aadhaarCard')} placeholder="Optional" />
              </div>

              <div className={`${styles.formGroup} ${styles.formGridFull}`}>
                <label className={styles.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FiGift size={13} /> Special Dates
                </label>
                <datalist id="specialDateLabels">
                  <option value="Birthday" />
                  <option value="Anniversary" />
                  <option value="Death Anniversary" />
                </datalist>
                {form.specialDates.map((d, index) => (
                  <div className={styles.specialDateRow} key={index}>
                    <input
                      className={styles.input}
                      list="specialDateLabels"
                      placeholder="Label (e.g. Birthday)"
                      value={d.label}
                      onChange={(e) => updateSpecialDate(index, 'label', e.target.value)}
                    />
                    <input
                      className={styles.input}
                      type="date"
                      value={d.date}
                      onChange={(e) => updateSpecialDate(index, 'date', e.target.value)}
                    />
                    <button
                      type="button"
                      className={styles.specialDateRemoveBtn}
                      onClick={() => removeSpecialDate(index)}
                      aria-label="Remove date"
                    >
                      <FiX size={14} />
                    </button>
                  </div>
                ))}
                <button type="button" className={`${styles.btn} ${styles.btnEdit}`} onClick={addSpecialDate}>
                  <FiPlus size={12} /> Add Date
                </button>
              </div>
            </div>

            <div className={styles.modalActions}>
              <button className={`${styles.btn} ${styles.btnEdit}`} onClick={() => setShowModal(false)}>
                Cancel
              </button>
              <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className={styles.modal}>
          <div className={styles.modalBox}>
            <div className={styles.modalTitle}>Remove {deleteTarget.name}?</div>
            <p className={styles.modalText}>
              This cannot be undone. Persons with existing offerings or receipts cannot be removed.
            </p>
            <div className={styles.modalActions}>
              <button className={`${styles.btn} ${styles.btnEdit}`} onClick={() => setDeleteTarget(null)}>
                Cancel
              </button>
              <button className={`${styles.btn} ${styles.btnDelete}`} onClick={handleDelete}>
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {phoneVerifyTarget && (
        <PersonPhoneVerificationModal
          personId={phoneVerifyTarget.id}
          phone={phoneVerifyTarget.phone}
          onClose={() => setPhoneVerifyTarget(null)}
          onVerified={load}
        />
      )}
    </div>
  );
}
